<template>
	<v-sheet class="pa-4" rounded outlined>
		<v-row>
			<v-col cols="12" class="title mt-0">
				Notificaciones
			</v-col>
			<v-col cols="12">
				Cargue el archivo excel para enviar notificaciones masivamente.
				<v-btn depressed @click="descargarPlantilla" color="primary">
					<v-icon>
						insert_drive_file
					</v-icon>
					Descargar plantilla
				</v-btn>
			</v-col>
			<v-col cols="12">
				<v-file-input label="Excel" :show-size="true" prepend-inner-icon="insert_drive_file" 
					clearable outlined small v-model="archivo" :disabled="preprocesando || enviando || deteniendo" prepend-icon="">
				</v-file-input>
				<v-btn depressed @click="preprocesarArchivo" :disabled="!archivo || enviando || deteniendo" :loading="preprocesando" color="primary">
					<v-icon>
						fact_check
					</v-icon>
					Preprocesar
				</v-btn>
				<v-btn depressed @click="limpiarNotificaciones" :disabled="!hayNotificaciones || enviando || deteniendo || preprocesando" color="primary">
					<v-icon>
						clear
					</v-icon>
					Limpiar
				</v-btn>
			</v-col>
			<v-col cols="12">
				<v-slider v-model="numeroEnviosParalelos" label="Número de envíos paralelos" step="1" :min="1" :max="numeroMaximoEnviosParalelos" 
					ticks :disabled="enviando || deteniendo">
					<template v-slot:append>
						{{ numeroEnviosParalelos }}
					</template>
				</v-slider>
			</v-col>
			<v-col cols="12">
				<v-card class="elevation-1 pa-2">
					<v-data-table :headers="encabezadosNotificaciones" :items="notificaciones" :footer-props="pieNotificaciones"
						:search="busquedaTabla" :custom-filter="filtroTabla">
						<template v-slot:item.rutdv="{ item }">
							{{ item.data.rut }}-{{ item.data.dv }}
						</template>
						<template v-slot:item.mensaje="{ item }">
							{{ item.data.mensaje }}
						</template>
						<template v-slot:item.estado="{ item }">
							<v-icon left small v-if="!item.estado.progreso" :color="item.estado.color">
								{{ item.estado.icono }}
							</v-icon>
							<v-progress-circular :size="12" color="primary" indeterminate :color="item.estado.color" class="mr-3" v-else/>
							{{ item.estado.nombre }}
						</template>
						<template v-slot:item.excepcion="{ item }">
							{{ item.excepcion }}
						</template>
					</v-data-table>
					<v-select v-model="estadoNotificacion" :items="estadosNotificacion" label="Filtrar por estado" dense solo 
						item-value="nombre" clearable hide-details multiple return-object>
						<template v-slot:item="{ item }">
							<v-icon left small :color="item.color">
								{{ item.icono }}
							</v-icon>
							{{ item.nombre }}
						</template>
						<template v-slot:selection="{ item }">
							<v-icon left small :color="item.color" class="mr-4">
								{{ item.icono }}
							</v-icon>
							{{ item.nombre }}
						</template>
					</v-select>
				</v-card>
			</v-col>
			<v-col cols="12">
				<v-expand-transition>
					<v-alert dense type="warning" v-show="hayNotificacionesInvalidas" class="my-1">
						El archivo cargado tiene ítemes inválidos.
						Corríjalos y vuelva a cargarlo.
					</v-alert>
				</v-expand-transition>
				<v-expand-transition>
					<v-progress-linear v-model="progresoEnvio" height="25" v-show="enviando" color="blue-grey lighten-2" rounded>
						<span v-if="enviando && !deteniendo">
							Enviando {{numeroNotificaciones - numeroNotificacionesCola}}/{{numeroNotificaciones}} ({{progresoEnvio}}%)...
						</span>
						<span v-if="deteniendo">
							Deteniendo...
						</span>
					</v-progress-linear>
				</v-expand-transition>
				<v-expand-transition>
					<v-alert dense type="warning" v-show="hayNotificacionesError" class="my-1">
						Hay <b>{{ numeroNotificacionesError }} {{ numeroNotificacionesError > 1 ? 'notificaciones' : 'notificación' }}</b> cuyo envío falló.
						<span v-if="enviando">
							Puede reintentar haciendo clic en <b>Detener</b> y luego en <b>Enviar notificaciones</b> ahora mismo, o reintentar cuando finalice el proceso.
						</span>
						<span v-else>
							Puede reintentar haciendo clic en <b>Enviar notificaciones</b>.
						</span>
					</v-alert>
				</v-expand-transition>
				<v-expand-transition>
					<v-alert dense type="success" v-show="todasNotificacionesEnviadas" class="my-1">
						Se enviaron todas las notificaciones.
					</v-alert>
				</v-expand-transition>
			</v-col>
			<v-col cols="12">
				<v-btn depressed :disabled="!hayNotificaciones || deteniendo || hayNotificacionesInvalidas || todasNotificacionesEnviadas" 
					@click="enviarNotificaciones" :loading="enviando" color="primary">
					<v-icon>
						send
					</v-icon>
					Enviar notificaciones
				</v-btn>
				<v-btn depressed @click="detenerEnvio" :disabled="!enviando" :loading="deteniendo" color="error">
					<v-icon>
						pan_tool
					</v-icon>
					Detener
				</v-btn>
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn depressed @click="descargarNotificaciones" :disabled="!hayNotificaciones || deteniendo || enviando" color="primary" v-bind="attrs" v-on="on">
							<v-icon>
								text_snippet
							</v-icon>
							Descargar reporte
						</v-btn>
					</template>
					Genera un archivo Excel con los itemes del listado superior (datos de notificaciones y sus estados actuales de envío).
				</v-tooltip>
			</v-col>
		</v-row>
	</v-sheet>
</template>

<script>
//import * as AsyncExecUtils from '@/library/utils/async-exec';
//import * as config from '@/config';
import * as ApiBoletasObjects from '@/library/IntegracionApiBoletas/Objects';
import XLSX from 'xlsx';
import { API } from 'aws-amplify';

// Estados de notificación
const INVALIDA = {
	'nombre': 'Inválida',
	'icono': 'report_problem',
	'color': 'warning'
};
const LISTA_PARA_ENVIO = {
	'nombre': 'Lista para envío', 
	'icono': 'hourglass_bottom'
};
const ENVIANDO = {
	'nombre': 'Enviando', 
	'progreso': true, 
	'icono': 'send'
};
const ENVIADA = {
	'nombre': 'Enviada', 
	'icono': 'check_circle',
	'color': 'success'
};
const ERROR = {
	'nombre': 'Error', 
	'icono': 'disabled_by_default', 
	'color': 'error'
};

// Configuración Excel
const EXCEL_FILE_ATTRS = {
	'sheetHeaders': ['rut', 'dv', 'mensaje'],
	'fileName': 'notificaciones.xlsx',
	'sheetTitle': 'Notificaciones',
};
const EXCEL_FILE_REPORT_ATTRS = {
	'sheetHeaders': ['rut', 'dv', 'mensaje', 'estado', 'excepcion'],
	'fileName': 'reporte envio notificaciones.xlsx',
	'sheetTitle': 'Reporte envío Notificaciones',
};

// Configuración validación mensajes
const MENSAJE_MAX_LENGTH = 128;

// Configuración de envíos paralelos
const DEFAULT_PARALLEL_SENDS = 15;
const MAX_PARALLEL_SENDS = 30;

// Configuración tabla
const ENCABEZADOS_TABLA = [
	{ text: 'Rut con Dv', value: 'rutdv', sortable: false },
	{ text: 'Mensaje', value: 'mensaje', sortable: false },
	{ text: 'Estado', value: 'estado', sortable: false, 'filterable': true},
	{ text: 'Excepcion', value: 'excepcion', sortable: false },
];
const PIE_TABLA = {'items-per-page-options': [10, 25, 50, 100, -1]};
const FILTRO_TABLA = function(value, search, item) {
	// search está stringified, es un arreglo de estados de notificación
	let parsedSearch = JSON.parse(search);
	if(parsedSearch.length === 0){ return true; } // Si no hay estados para filtrar, aceptar
	let itemMatches = parsedSearch.reduce(function(x,y){ return x || (item.estado.nombre === y.nombre); }, false);
	return itemMatches;
};


export default {
	components: {
		
	},
	props: {
		
	},
	data () {
		return {
			preprocesando: false,
			archivo: undefined,
			notificaciones: [], //{data: {rut, dv, mensaje}, estado: {nombre, icono, color}, comentario}
			colaNotificaciones: [],
			enviando: false,
			deteniendo: false,
			detenerEnvio: undefined, // función
			numeroEnviosParalelos: DEFAULT_PARALLEL_SENDS,
			numeroMaximoEnviosParalelos: MAX_PARALLEL_SENDS,
			encabezadosNotificaciones: ENCABEZADOS_TABLA,
			pieNotificaciones: PIE_TABLA,
			estadosNotificacion: [LISTA_PARA_ENVIO, ENVIANDO, ENVIADA, INVALIDA, ERROR],
			estadoNotificacion: [],
			filtroTabla: FILTRO_TABLA,
		};
	},
	computed: {
		busquedaTabla: function() {
			let vm = this;
			let estadoNotificacion = vm.estadoNotificacion;
			return JSON.stringify(estadoNotificacion); // search de datatable solo soporta cadena
		},
		numeroNotificacionesCola: function() {
			let vm = this;
			return vm.colaNotificaciones.length;
		},
		numeroNotificaciones: function() {
			let vm = this;
			return vm.notificaciones.length;
		},
		hayNotificaciones: function() {
			let vm = this;
			return (vm.numeroNotificaciones > 0);
		},
		todasNotificacionesEnviadas: function() {
			let vm = this;
			return (vm.numeroNotificacionesEnviadas === vm.numeroNotificaciones) && vm.hayNotificaciones;
		},
		numeroNotificacionesError: function() {
			let vm = this;
			let numero = vm.notificaciones.reduce(function(n, x) { return n + ((x.estado === ERROR) ? 1 : 0); }, 0);
			return numero;
		},
		hayNotificacionesError: function() {
			let vm = this;
			return (vm.numeroNotificacionesError > 0);
		},
		numeroNotificacionesInvalidas: function() {
			let vm = this;
			let numero = vm.notificaciones.reduce(function(n, x) { return n + ((x.estado === INVALIDA) ? 1 : 0); }, 0);
			return numero;
		},
		hayNotificacionesInvalidas: function() {
			let vm = this;
			return (vm.numeroNotificacionesInvalidas > 0);
		},
		numeroNotificacionesEnviadas: function() {
			let vm = this;
			let numero = vm.notificaciones.reduce(function(n, x) { return n + ((x.estado === ENVIADA) ? 1 : 0); }, 0);
			return numero;
		},
		progresoEnvio: function() {
			let vm = this;
			let n = vm.notificaciones.length;
			let c = vm.colaNotificaciones.length;
			return Math.ceil(100.0*(n - c)/n);
		},
	},
	methods: {
		descargarPlantilla: function() {
			let items = [];
			let book = XLSX.utils.book_new();
			let includeHeaders = true;
			let sheetConfig = {
				'header': EXCEL_FILE_ATTRS.sheetHeaders,
				'skipHeader': !includeHeaders,
			};
			let sheet = XLSX.utils.json_to_sheet(items, sheetConfig);
			XLSX.utils.book_append_sheet(book, sheet, EXCEL_FILE_ATTRS.sheetTitle);
			XLSX.writeFile(book, EXCEL_FILE_ATTRS.fileName);
		},
		preprocesarArchivo: function() {
			let vm = this;
			vm.limpiarBusqueda();
			let readExcelItems = function(itemsProcessorCallback) {
				let reader = new FileReader();
				let onloadCallback = function(e) {
					let data = new Uint8Array(e.target.result);
					let workbook = XLSX.read(data, { 'type': 'array' });
					let firstWorksheetName = workbook.SheetNames[0];
					let worksheet = workbook.Sheets[firstWorksheetName];
					let options = { 'raw': true };
					let readItems = XLSX.utils.sheet_to_json(worksheet, options);
					vm.archivo = null; // Clear file input
					if( EXCEL_FILE_ATTRS.sheetTitle !== firstWorksheetName) {
						alert(`Al parecer el archivo excel que se intenta preprocesar no corresponde a uno de ${EXCEL_FILE_ATTRS.sheetTitle}.`);
						vm.preprocessing = false; 
						return;
					}
					if(readItems.length == 0) {
						let msg = 'Archivo excel al parecer no contiene datos. Revise el archivo e intente nuevamente.';
						alert(msg);
						vm.preprocessing = false; 
						return;
					}
					vm.excelItems = readItems;
					itemsProcessorCallback(readItems);
				};
				
				reader.onload = onloadCallback;
				reader.readAsArrayBuffer(vm.archivo);
			};
			
			let analizeExcelItems = function(readItems) {
				vm.notificaciones = [];
				
				let itemValido = function(item) {
					let rut = item.rut;
					let dv = item.dv;
					let mensaje = item.mensaje;
					
					let validarRut = function(x) {
						// Uno o más dígitos
						if(x === undefined){ return 'RUT debe definirse'; }
						if(`${x}`.match(/^\d+$/) === null) { return 'RUT debe contener dígitos'; }
						return true;
					};
					let validarDv = function(x) {
						// Dígito o k o K
						if(x === undefined){ return 'DV debe definirse'; }
						if(`${x}`.match(/^\d|K$/) === null) { return 'DV debe ser un dígito o K'; }
						return true;
					};
					let validarMensaje = function(x) {
						// Cadena no vacía de a lo más MENSAJE_MAX_LENGTH caracteres
						if(x === undefined) { return 'Mensaje debe definirse'; }
						if(x.length === 0) { return 'Mensaje debe definirse'; }
						if(x.length > MENSAJE_MAX_LENGTH) { return `Mensaje supera los ${MENSAJE_MAX_LENGTH} caracteres`; }
						return true;
					};
					let validarRutDv = function(rut, dv) {
						let dvCalculado = vm.calcularDv(rut);
						if(dvCalculado !== `${dv}`.toUpperCase()) {
							return 'DV no le corresponde al RUT';
						}
						return true;
					};
					
					let rutValido = validarRut(rut);
					if(rutValido !== true) { return rutValido; }
					let dvValido = validarDv(dv);
					if(dvValido !== true) { return dvValido; }
					let rutDvValido = validarRutDv(rut, dv);
					if(rutDvValido !== true) { return rutDvValido; }
					let mensajeValido = validarMensaje(mensaje);
					if(mensajeValido !== true) { return mensajeValido; }
					
					return true;
				};
				
				readItems.forEach(function(x){
					let valido = itemValido(x); // retorna true o una cadena si no es válido
					vm.notificaciones.push({
						'data': {
							'rut': x.rut,
							'dv': x.dv,
							'mensaje': x.mensaje,
						},
						'estado': (valido === true) ? LISTA_PARA_ENVIO : INVALIDA,
						'excepcion': (valido === true) ? undefined : valido,
					});
				});
				vm.preprocessing = false;
			};

			vm.preprocessing = true;
			readExcelItems(analizeExcelItems);
		},
		vaciarCola: function() {
			let vm = this;
			vm.colaNotificaciones.length = 0;
		},
		enviarNotificaciones: function() {
			let vm = this;
			let notificaciones = vm.notificaciones;
			vm.colaNotificaciones = notificaciones.filter(function(x) {
				let estadosEnviar = [LISTA_PARA_ENVIO, ERROR];
				return estadosEnviar.includes(x.estado);
			});
			let colaNotificaciones = vm.colaNotificaciones;
			let tamañoCola = colaNotificaciones.length;
			if(tamañoCola === 0) {
				alert('No hay notificaciones por enviar');
				return;
			}
			let numeroEnvios = Math.min(tamañoCola, vm.numeroEnviosParalelos);
			vm.enviando = true;
			vm.detenerEnvio = function() {
				// Simplemente vaciar cola
				vm.deteniendo = true;
				vm.vaciarCola();
			};
			let enviarNotificacion = function() {
				let item = colaNotificaciones.shift();
				let hayItem = (item !== undefined);
				if(!hayItem) {
					numeroEnvios--;
					if(numeroEnvios === 0) {
						vm.enviando = false;
						vm.deteniendo = false;
					}
					return;
				}
				item.estado = ENVIANDO;
				item.excepcion = undefined;
				let exito = function() {
					item.estado = ENVIADA;
					enviarNotificacion();
				};
				let error = function(errMsg) {
					item.estado = ERROR;
					item.excepcion = `Falló (${errMsg})`;
					enviarNotificacion();
				};
				let headers = {};
				let body = {
					'to': `${item.data.rut}-${item.data.dv}`,
					'data': {
						'mensaje': item.data.mensaje
					},
				};
				API.post('notificaciones', '/notificaciones/enviar', {body, headers})
				.then(response => {
				  exito();
				})
				.catch(err => {
					let errMsg;
					if(err && err.response && err.response.data) {
						errMsg = err.response.data;
					}
					else {
						errMsg = err ? err.toString() : 'Error desconocido';
					}
					error(errMsg);
				});
			};
			
			for(let i=0; i<numeroEnvios; i++) {
				enviarNotificacion();
			}
		},
		limpiarNotificaciones: function() {
			let vm = this;
			vm.notificaciones = [];
			vm.limpiarBusqueda();
		},
		limpiarBusqueda: function() {
			let vm = this;
			vm.busquedaTabla = undefined;
		},
		descargarNotificaciones: function() {
			let vm = this;
			let notificaciones = vm.notificaciones;
			
			// TODO generar excel
			let items = notificaciones.map(function(x) {
				return {
					'rut': x.data.rut, 
					'dv': x.data.dv, 
					'mensaje': x.data.mensaje, 
					'estado': x.estado.nombre,
					'excepcion': x.excepcion,
				};
			});
			let book = XLSX.utils.book_new();
			let includeHeaders = true;
			let sheetConfig = {
				'header': EXCEL_FILE_REPORT_ATTRS.sheetHeaders,
				'skipHeader': !includeHeaders,
			};
			let sheet = XLSX.utils.json_to_sheet(items, sheetConfig);
			XLSX.utils.book_append_sheet(book, sheet, EXCEL_FILE_REPORT_ATTRS.sheetTitle);
			XLSX.writeFile(book, EXCEL_FILE_REPORT_ATTRS.fileName);
		},
		calcularDv: function(rut) {
			// Determinar dv por fuerza bruta
			let dvs = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'K'];
			let dv;
			dvs.forEach(function(x) { if(ApiBoletasObjects.RUT.dvValido(rut, x)){ dv = x; } });
			if(dv === undefined) { throw `No se pudo computar dv para rut ${rut}`; }
			return dv;
		},
	},
	watch: {
		
	},
	mounted: function() {
		
	},
	created: function() {
		
	},
};
</script>